import { Component, ErrorInfo, ReactNode } from "react";
import { PODGO_VERSION_FALLBACK } from "@/version";
import { isChunkLoadError, reloadForStaleChunk } from "@/lib/lazyWithRetry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const CRASH_REPORT_KEY = "podgo_crash_report";
const LAST_CRASH_KEY = "podgo_last_crash";

export class GlobalErrorBoundary extends Component<Props, State> {
  private _caughtError: Error | null = null;
  private _caughtStack: string | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  private crashPayload() {
    return {
      error: this._caughtError?.message ?? "Unknown error",
      stack: this._caughtStack ?? this._caughtError?.stack ?? null,
      url: window.location.href,
      version: PODGO_VERSION_FALLBACK,
      at: new Date().toISOString(),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this._caughtError = error;
    this._caughtStack = info.componentStack ?? error.stack ?? null;
    console.error("[GlobalErrorBoundary]", error, info);

    // Keep the last crash around so support can read the real failure even
    // when the user reloads instead of filing a report.
    try {
      sessionStorage.setItem(LAST_CRASH_KEY, JSON.stringify(this.crashPayload()));
    } catch {
      // Storage unavailable — console output is the only record.
    }

    // A publish swaps every hashed chunk; an old tab then fails to import one.
    // Recover silently instead of showing the crash screen (guarded, no loop).
    if (isChunkLoadError(error)) reloadForStaleChunk();
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReport = () => {
    try {
      sessionStorage.setItem(CRASH_REPORT_KEY, JSON.stringify(this.crashPayload()));
    } catch {
      // Storage may be full or unavailable — proceed with reload anyway
    }
    window.location.reload();
  };


  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#010101",
          color: "#ffffff",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: "24px",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>
            Oops! Looks like something went wrong.
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "#a1a1a1",
              lineHeight: 1.6,
              marginBottom: 32,
            }}
          >
            We're sorry about that. You can reload the app to try again, or
            report the issue so our team can fix it.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={this.handleReload}
              style={{
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 8,
                border: "1px solid #333",
                backgroundColor: "transparent",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              Reload App
            </button>
            <button
              onClick={this.handleReport}
              style={{
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 8,
                border: "none",
                backgroundColor: "#6d28d9",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              Report Issue
            </button>
          </div>
        </div>
      </div>
    );
  }
}
